import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Ticket, Calendar, MapPin, Users, Mail, Phone, Search, ArrowLeft, 
  CheckCircle, Clock, XCircle, QrCode, Download, ExternalLink,
  MessageCircle, FileText, Shirt, Sparkles, Pill, Car, ChevronRight,
  Copy, AlertCircle, CreditCard, RefreshCw, Heart, Home, Navigation
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const LOGO_URL = "/logo-kairos.png";

const MinhaInscricaoPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [inscricoes, setInscricoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
      handleSearch(emailParam);
    }
  }, [searchParams]);

  const handleSearch = async (searchEmail = email) => {
    if (!searchEmail || !searchEmail.includes('@')) {
      toast.error('Por favor, informe um email válido');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const response = await fetch(`${API_URL}/api/minha-inscricao?email=${encodeURIComponent(searchEmail)}`);
      
      if (response.ok) {
        const data = await response.json();
        setInscricoes(data);
        if (data.length === 0) {
          toast.info('Nenhuma inscrição encontrada');
        }
      } else if (response.status === 404) {
        setInscricoes([]);
        toast.info('Nenhuma inscrição encontrada para este email');
      } else {
        throw new Error('Erro ao buscar inscrição');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao buscar inscrição');
      setInscricoes([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatEventPeriod = (dataInicio, dataFim) => {
    try {
      const inicio = new Date(dataInicio);
      const fim = new Date(dataFim);
      const diaInicio = inicio.getDate();
      const diaFim = fim.getDate();
      const mes = format(inicio, "MMMM", { locale: ptBR });
      const ano = inicio.getFullYear();
      return `${diaInicio} a ${diaFim} de ${mes} de ${ano}`;
    } catch {
      return "Data a confirmar";
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código copiado!');
  };

  const downloadQRCode = (participanteId) => {
    const svg = document.getElementById(`qr-${participanteId}`);
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = `qrcode-kairos-2026.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const generateGoogleCalendarUrl = (evento) => {
    if (!evento) return '#';
    
    const startDate = new Date(evento.data_inicio);
    const endDate = new Date(evento.data_fim);
    
    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const title = encodeURIComponent(evento.nome_evento || 'Encontro de Casais Kairós 2026');
    const location = encodeURIComponent(evento.local || 'Hotel Fazenda Minuano');
    const details = encodeURIComponent('Encontro de Casais Kairós 2026 - Seminário Psicoteológico');
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}&location=${location}&details=${details}`;
  };

  const getGoogleMapsUrl = (local) => {
    return `https://www.google.com/maps/search/${encodeURIComponent(local || 'Hotel Fazenda Minuano Ji-Parana RO')}`;
  };

  // Checklist items
  const checklistItems = [
    { icon: FileText, text: "Documento pessoal (RG ou CNH)" },
    { icon: Sparkles, text: "Itens de higiene pessoal" },
    { icon: Shirt, text: "Roupas confortáveis" },
    { icon: Pill, text: "Remédios de uso contínuo" },
    { icon: Clock, text: "Chegar até 17h no primeiro dia" }
  ];

  // Status config
  const getStatusConfig = (status) => {
    const configs = {
      aprovado: { 
        icon: CheckCircle, 
        bg: 'bg-[var(--ds-success)]/10',
        border: 'border-[var(--ds-success)]/30',
        text: 'text-[var(--ds-success)]',
        label: 'Inscrição Confirmada',
        sublabel: 'Pagamento aprovado'
      },
      pendente: { 
        icon: Clock, 
        bg: 'bg-[var(--ds-warning)]/10',
        border: 'border-[var(--ds-warning)]/30',
        text: 'text-[var(--ds-warning)]',
        label: 'Pagamento Pendente',
        sublabel: 'Aguardando confirmação'
      },
      cancelado: { 
        icon: XCircle, 
        bg: 'bg-[var(--ds-danger)]/10',
        border: 'border-[var(--ds-danger)]/30',
        text: 'text-[var(--ds-danger)]',
        label: 'Pagamento Cancelado',
        sublabel: 'Entre em contato para suporte'
      },
    };
    return configs[status] || configs.pendente;
  };

  return (
    <>
      <Helmet>
        <title>Minha Inscrição | Encontro de Casais Kairós 2026</title>
        <meta name="description" content="Acesse sua inscrição do Encontro de Casais Kairós 2026. Consulte seus dados, QR Code para check-in e informações do evento." />
      </Helmet>

      <div className="min-h-screen bg-[var(--ds-background)]" data-testid="minha-inscricao-page">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--ds-background)]/90 backdrop-blur-md border-b border-[var(--ds-border)]">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-[var(--ds-text-secondary)]" />
              <img src={LOGO_URL} alt="Kairós" className="w-10 h-10 rounded-full object-cover" />
              <span className="font-display text-xl text-[var(--ds-text-primary)]">Kairós</span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-[var(--ds-border)] text-[var(--ds-text-primary)] hover:bg-[var(--ds-surface-hover)] rounded-full px-6 h-10">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </Link>
          </div>
        </nav>

        <div className="pt-24 pb-12">
          {/* Search Section */}
          {!searched || inscricoes.length === 0 ? (
            <div className="container mx-auto px-4 max-w-xl">
              <div className="text-center mb-10">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[var(--ds-primary)] to-[var(--ds-primary-dark)] flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(255,122,0,0.3)]">
                  <Ticket className="w-10 h-10 text-white" />
                </div>
                <h1 className="font-display text-3xl md:text-4xl text-[var(--ds-text-primary)] mb-4">
                  Portal do Participante
                </h1>
                <p className="text-[var(--ds-text-secondary)]">
                  Informe o e-mail cadastrado para acessar sua inscrição
                </p>
              </div>
              
              <Card className="bg-[var(--ds-surface)] border-[var(--ds-border)]">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--ds-text-muted)]" />
                      <Input
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="h-14 pl-12 bg-[var(--ds-background)] border-[var(--ds-border)] text-[var(--ds-text-primary)] placeholder:text-[var(--ds-text-muted)] text-lg"
                        data-testid="email-search-input"
                      />
                    </div>
                    <Button
                      onClick={() => handleSearch()}
                      disabled={loading}
                      className="w-full h-14 bg-gradient-to-r from-[var(--ds-primary)] to-[var(--ds-primary-light)] text-white font-semibold text-lg rounded-xl shadow-[0_0_20px_rgba(255,122,0,0.3)]"
                      data-testid="search-btn"
                    >
                      {loading ? (
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Search className="w-5 h-5 mr-2" />
                          Buscar Inscrição
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {searched && inscricoes.length === 0 && (
                <Card className="mt-6 bg-[var(--ds-surface)] border-[var(--ds-border)]">
                  <CardContent className="py-12 text-center">
                    <Ticket className="w-16 h-16 text-[var(--ds-text-muted)] mx-auto mb-4" />
                    <h3 className="text-[var(--ds-text-primary)] font-semibold text-xl mb-2">
                      Nenhuma inscrição encontrada
                    </h3>
                    <p className="text-[var(--ds-text-secondary)] mb-6">
                      Verifique se o email está correto ou faça sua inscrição
                    </p>
                    <Link to="/">
                      <Button className="bg-[var(--ds-primary)] text-white rounded-full px-8">
                        Fazer Inscrição
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            /* Portal Premium - Quando encontrou inscrição */
            inscricoes.map(({ participante, evento, lote }, index) => {
              const statusConfig = getStatusConfig(participante.status_pagamento);
              const StatusIcon = statusConfig.icon;
              const isApproved = participante.status_pagamento === 'aprovado';
              const isPending = participante.status_pagamento === 'pendente';

              return (
                <div key={participante.id} className="container mx-auto px-4 max-w-4xl space-y-8">
                  
                  {/* ══════════════════════════════════════════════════════════════════════
                      1️⃣ HERO PERSONALIZADO
                  ══════════════════════════════════════════════════════════════════════ */}
                  <section className="text-center pt-8">
                    <h1 className="font-display text-3xl md:text-4xl text-[var(--ds-text-primary)] mb-2">
                      Olá, {participante.nome_responsavel?.split(' ')[0]} 👋
                    </h1>
                    <p className="text-[var(--ds-text-secondary)] text-lg mb-6">
                      Sua inscrição no Kairós 2026
                    </p>
                    
                    {/* Status Badge Grande */}
                    <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl ${statusConfig.bg} border-2 ${statusConfig.border}`}>
                      <StatusIcon className={`w-8 h-8 ${statusConfig.text}`} />
                      <div className="text-left">
                        <p className={`font-bold text-xl ${statusConfig.text}`}>{statusConfig.label}</p>
                        <p className="text-[var(--ds-text-secondary)] text-sm">{statusConfig.sublabel}</p>
                      </div>
                    </div>
                  </section>

                  {/* ══════════════════════════════════════════════════════════════════════
                      2️⃣ SEUS DADOS
                  ══════════════════════════════════════════════════════════════════════ */}
                  <Card className="bg-[var(--ds-surface)] border-[var(--ds-border)]">
                    <CardContent className="p-6">
                      <h2 className="text-[var(--ds-text-primary)] font-semibold text-lg mb-6 flex items-center gap-2">
                        <Users className="w-5 h-5 text-[var(--ds-primary)]" />
                        Seus Dados
                      </h2>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-[var(--ds-background)] rounded-xl border border-[var(--ds-border)]">
                          <p className="text-[var(--ds-text-muted)] text-xs uppercase tracking-wider mb-1">Responsável</p>
                          <p className="text-[var(--ds-text-primary)] font-medium text-lg">{participante.nome_responsavel}</p>
                        </div>
                        <div className="p-4 bg-[var(--ds-background)] rounded-xl border border-[var(--ds-border)]">
                          <p className="text-[var(--ds-text-muted)] text-xs uppercase tracking-wider mb-1">Cônjuge</p>
                          <p className="text-[var(--ds-text-primary)] font-medium text-lg">{participante.nome_conjuge}</p>
                        </div>
                        <div className="p-4 bg-[var(--ds-background)] rounded-xl border border-[var(--ds-border)]">
                          <p className="text-[var(--ds-text-muted)] text-xs uppercase tracking-wider mb-1">E-mail</p>
                          <p className="text-[var(--ds-text-secondary)] flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            {participante.email}
                          </p>
                        </div>
                        <div className="p-4 bg-[var(--ds-background)] rounded-xl border border-[var(--ds-border)]">
                          <p className="text-[var(--ds-text-muted)] text-xs uppercase tracking-wider mb-1">Telefone</p>
                          <p className="text-[var(--ds-text-secondary)] flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            {participante.telefone}
                          </p>
                        </div>
                        {participante.cidade && (
                          <div className="p-4 bg-[var(--ds-background)] rounded-xl border border-[var(--ds-border)]">
                            <p className="text-[var(--ds-text-muted)] text-xs uppercase tracking-wider mb-1">Cidade</p>
                            <p className="text-[var(--ds-text-secondary)] flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              {participante.cidade}
                            </p>
                          </div>
                        )}
                        {participante.codigo_ingresso && (
                          <div className="p-4 bg-gradient-to-r from-[var(--ds-primary)]/10 to-[var(--ds-primary)]/5 rounded-xl border-2 border-[var(--ds-primary)]/30">
                            <p className="text-[var(--ds-primary)] text-xs uppercase tracking-wider mb-1 font-medium">Código do Ingresso</p>
                            <div className="flex items-center justify-between">
                              <p className="text-[var(--ds-primary)] font-mono font-bold text-2xl">{participante.codigo_ingresso}</p>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyToClipboard(participante.codigo_ingresso)}
                                className="text-[var(--ds-primary)] hover:bg-[var(--ds-primary)]/10"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* ══════════════════════════════════════════════════════════════════════
                      3️⃣ RESUMO DO EVENTO
                  ══════════════════════════════════════════════════════════════════════ */}
                  {evento && (
                    <Card className="bg-[var(--ds-surface)] border-[var(--ds-border)]">
                      <CardContent className="p-6">
                        <h2 className="text-[var(--ds-text-primary)] font-semibold text-lg mb-6 flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-[var(--ds-secondary)]" />
                          Resumo do Evento
                        </h2>
                        
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-[var(--ds-background)] rounded-xl border border-[var(--ds-border)]">
                              <p className="text-[var(--ds-text-muted)] text-xs uppercase tracking-wider mb-1">Data</p>
                              <p className="text-[var(--ds-text-primary)] font-medium">{formatEventPeriod(evento.data_inicio, evento.data_fim)}</p>
                            </div>
                            <div className="p-4 bg-[var(--ds-background)] rounded-xl border border-[var(--ds-border)]">
                              <p className="text-[var(--ds-text-muted)] text-xs uppercase tracking-wider mb-1">Local</p>
                              <p className="text-[var(--ds-text-primary)] font-medium">{evento.local}</p>
                            </div>
                            <div className="p-4 bg-[var(--ds-success)]/10 rounded-xl border border-[var(--ds-success)]/30">
                              <p className="text-[var(--ds-success)] text-xs uppercase tracking-wider mb-1 font-medium">Check-in</p>
                              <p className="text-[var(--ds-success)] font-bold">{evento.checkin_hora || "09/10/2026 às 15h"}</p>
                            </div>
                            <div className="p-4 bg-[var(--ds-danger)]/10 rounded-xl border border-[var(--ds-danger)]/30">
                              <p className="text-[var(--ds-danger)] text-xs uppercase tracking-wider mb-1 font-medium">Check-out</p>
                              <p className="text-[var(--ds-danger)] font-bold">{evento.checkout_hora || "12/10/2026 até 14h"}</p>
                            </div>
                          </div>
                          
                          <a 
                            href={getGoogleMapsUrl(evento.local)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full p-4 bg-[var(--ds-secondary)]/10 hover:bg-[var(--ds-secondary)]/20 border border-[var(--ds-secondary)]/30 rounded-xl text-[var(--ds-secondary)] font-medium transition-colors"
                          >
                            <Navigation className="w-5 h-5" />
                            Abrir rota no Google Maps
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ══════════════════════════════════════════════════════════════════════
                      4️⃣ QR CODE (SE APROVADO)
                  ══════════════════════════════════════════════════════════════════════ */}
                  {isApproved && participante.qr_token && (
                    <Card className="bg-gradient-to-br from-[var(--ds-success)]/5 to-[var(--ds-success)]/10 border-2 border-[var(--ds-success)]/30">
                      <CardContent className="p-8">
                        <div className="text-center">
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ds-success)]/20 border border-[var(--ds-success)]/30 mb-6">
                            <QrCode className="w-5 h-5 text-[var(--ds-success)]" />
                            <span className="text-[var(--ds-success)] font-medium">QR Code para Check-in</span>
                          </div>
                          
                          <div className="inline-block p-6 bg-white rounded-3xl shadow-[0_0_40px_rgba(0,200,83,0.2)] mb-6">
                            <QRCodeSVG
                              id={`qr-${participante.id}`}
                              value={`${window.location.origin}/admin/checkin?token=${participante.qr_token}`}
                              size={220}
                              level="H"
                              includeMargin={true}
                            />
                          </div>
                          
                          <p className="text-[var(--ds-text-secondary)] mb-6 max-w-md mx-auto">
                            Apresente este QR Code na <span className="text-[var(--ds-text-primary)] font-medium">recepção do hotel</span> no momento do check-in.
                          </p>
                          
                          <Button 
                            onClick={() => downloadQRCode(participante.id)}
                            className="bg-[var(--ds-success)] hover:bg-[var(--ds-success-dark)] text-white rounded-full px-8"
                          >
                            <Download className="w-5 h-5 mr-2" />
                            Baixar QR Code
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ══════════════════════════════════════════════════════════════════════
                      8️⃣ PAGAMENTO PENDENTE (SE PENDENTE)
                  ══════════════════════════════════════════════════════════════════════ */}
                  {isPending && (
                    <Card className="bg-[var(--ds-warning)]/5 border-2 border-[var(--ds-warning)]/30">
                      <CardContent className="p-8">
                        <div className="flex flex-col items-center text-center">
                          <div className="w-16 h-16 rounded-2xl bg-[var(--ds-warning)]/20 flex items-center justify-center mb-4">
                            <AlertCircle className="w-8 h-8 text-[var(--ds-warning)]" />
                          </div>
                          
                          <h3 className="text-[var(--ds-text-primary)] font-semibold text-xl mb-2">
                            Pagamento Pendente
                          </h3>
                          <p className="text-[var(--ds-text-secondary)] mb-6 max-w-md">
                            Seu pagamento ainda não foi confirmado. Complete o pagamento para garantir sua vaga.
                          </p>
                          
                          <div className="flex flex-col sm:flex-row gap-4">
                            <Link to={evento ? `/inscricao/${evento.id}` : '/inscricao'}>
                              <Button className="bg-[var(--ds-warning)] hover:bg-[var(--ds-warning-dark)] text-black font-semibold rounded-full px-8">
                                <CreditCard className="w-5 h-5 mr-2" />
                                Finalizar Pagamento
                              </Button>
                            </Link>
                            <Button 
                              variant="outline"
                              onClick={() => handleSearch()}
                              className="border-[var(--ds-border)] text-[var(--ds-text-secondary)] rounded-full px-6"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Verificar Status
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ══════════════════════════════════════════════════════════════════════
                      5️⃣ ADICIONAR AO CALENDÁRIO
                  ══════════════════════════════════════════════════════════════════════ */}
                  {isApproved && evento && (
                    <Card className="bg-[var(--ds-surface)] border-[var(--ds-border)]">
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[var(--ds-secondary)]/10 flex items-center justify-center">
                              <Calendar className="w-6 h-6 text-[var(--ds-secondary)]" />
                            </div>
                            <div>
                              <h3 className="text-[var(--ds-text-primary)] font-medium">Não esqueça a data!</h3>
                              <p className="text-[var(--ds-text-secondary)] text-sm">Adicione o evento ao seu calendário</p>
                            </div>
                          </div>
                          <a 
                            href={generateGoogleCalendarUrl(evento)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button className="bg-[var(--ds-secondary)] hover:bg-[var(--ds-secondary-dark)] text-white rounded-full px-6">
                              <Calendar className="w-5 h-5 mr-2" />
                              Adicionar ao Google Calendar
                              <ExternalLink className="w-4 h-4 ml-2" />
                            </Button>
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ══════════════════════════════════════════════════════════════════════
                      6️⃣ PREPARE-SE PARA O ENCONTRO
                  ══════════════════════════════════════════════════════════════════════ */}
                  {isApproved && (
                    <Card className="bg-[var(--ds-surface)] border-[var(--ds-border)]">
                      <CardContent className="p-6">
                        <h2 className="text-[var(--ds-text-primary)] font-semibold text-lg mb-6 flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-[var(--ds-primary)]" />
                          Prepare-se para o Encontro
                        </h2>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {checklistItems.map((item, i) => (
                            <div 
                              key={i}
                              className="flex items-center gap-3 p-4 bg-[var(--ds-background)] rounded-xl border border-[var(--ds-border)]"
                            >
                              <div className="w-10 h-10 rounded-lg bg-[var(--ds-primary)]/10 flex items-center justify-center flex-shrink-0">
                                <item.icon className="w-5 h-5 text-[var(--ds-primary)]" />
                              </div>
                              <span className="text-[var(--ds-text-secondary)]">{item.text}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-6 p-4 bg-[var(--ds-info)]/10 border border-[var(--ds-info)]/30 rounded-xl">
                          <p className="text-[var(--ds-info)] text-sm">
                            <span className="font-medium">Dica:</span> Chegue com tempo para fazer o check-in com calma e conhecer o espaço antes da abertura oficial.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ══════════════════════════════════════════════════════════════════════
                      7️⃣ SUPORTE DIRETO
                  ══════════════════════════════════════════════════════════════════════ */}
                  <Card className="bg-[var(--ds-surface)] border-[var(--ds-border)]">
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                            <MessageCircle className="w-6 h-6 text-green-500" />
                          </div>
                          <div>
                            <h3 className="text-[var(--ds-text-primary)] font-medium">Precisa de ajuda?</h3>
                            <p className="text-[var(--ds-text-secondary)] text-sm">Fale conosco pelo WhatsApp</p>
                          </div>
                        </div>
                        <a 
                          href="https://wa.me/5569999999999"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full px-6">
                            <MessageCircle className="w-5 h-5 mr-2" />
                            Falar no WhatsApp
                          </Button>
                        </a>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Links rápidos */}
                  <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                    <Link to="/programacao" className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-primary)] transition-colors text-sm flex items-center gap-1">
                      Ver Programação
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                    <Link to="/local" className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-primary)] transition-colors text-sm flex items-center gap-1">
                      Ver Local
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                    <Link to="/o-que-inclui" className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-primary)] transition-colors text-sm flex items-center gap-1">
                      O que está Incluso
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-[var(--ds-border)] mt-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Kairós" className="w-8 h-8 rounded-full object-cover" />
              <span className="text-[var(--ds-text-primary)] font-medium">Encontro de Casais Kairós</span>
            </div>
            <p className="text-[var(--ds-text-muted)] text-sm mb-4">
              © {new Date().getFullYear()} Kairós. Todos os direitos reservados.
            </p>
            <p className="text-[var(--ds-text-muted)] text-xs">
              Desenvolvido por{' '}
              <a 
                href="https://grooverdigital.com.br" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[var(--ds-primary)] hover:text-[var(--ds-primary-light)] transition-colors font-medium"
              >
                Groover Digital e Tecnologia
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default MinhaInscricaoPage;
